import { Avatar, Box, Card, Chip, Grid, Skeleton, Typography } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditButton from '../EditButton/EditButton';
import { FC, ReactNode, useEffect, useState } from 'react';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchUserById } from "../../store/userThunk";
import { PermissionsEnum } from '../../utils/auth.types';
import UserFormDialog from '../../pages/User/UserFormDialog';
import UserPasswordDialog from '../../pages/User/UserPasswordDialog';

const cardSx = {
  borderRadius: '8px',
  boxShadow: 'none',
  border: '1px solid #dcdcdc',
  height: '100%',
};

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('');

const SectionCard = ({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) => (
  <Card sx={cardSx}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.5, bgcolor: '#e9ecf1', borderBottom: '1px solid #dcdcdc', color: '#374151' }}>
      {icon}
      <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1a1a1a' }}>{title}</Typography>
    </Box>
    <Box sx={{ px: 3, py: 1 }}>{children}</Box>
  </Card>
);

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.75, '&:not(:last-of-type)': { borderBottom: '1px solid #e4e4e4' } }}>
    <Typography sx={{ color: '#6b7280', fontSize: '14px', flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ color: '#1a1a1a', fontSize: '14px', fontWeight: 400, textAlign: 'right', wordBreak: 'break-word' }}>{children}</Box>
  </Box>
);

const UserBasicInfo: FC<any> = (props: { userId: any }) => {

  const userId = props.userId;

  const dispatch = useAppDispatch();
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const { userById } = useAppSelector(state => state.users);
  const { permissions, user: authUser, modulePermissions } = useAppSelector(state => state.auth);
  const canUpdateUsers = permissions?.includes(PermissionsEnum.Users_Update);
  // Setting someone's password is admin-only, mirroring the API gate.
  const actorRoleId = (authUser as any)?.role?.[0]?.roleId;
  const isAdmin = !!modulePermissions?.isAccountOwner || (typeof actorRoleId === 'number' && actorRoleId <= 2);

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserById(userId));
    }
  }, [dispatch, userId]);

  // Avoid flashing a previously viewed member while the new one loads
  const user = (userById?._id === userId ? userById : null) as any;
  const roleName: string | undefined = user ? user['role']?.role : undefined;
  const signInMethods = ['google', 'microsoft', 'sso', 'email'].filter(method => user?.auth?.[method]);
  // Member API returns bare account ids, so resolve names from the logged-in user's accounts
  const knownAccounts: any[] = (authUser as any)?.accounts || [];
  const accounts: any[] = (user?.accounts || []).map((account: any) => {
    const id = typeof account === 'string' ? account : account?._id;
    const known = knownAccounts.find(a => a._id === id);
    return { _id: id, name: account?.name || account?.legalName || known?.name || known?.legalName || 'Company' };
  });

  if (!user) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}><Skeleton variant="rounded" height={140} /></Grid>
        <Grid item xs={12} md={6}><Skeleton variant="rounded" height={240} /></Grid>
        <Grid item xs={12} md={6}><Skeleton variant="rounded" height={240} /></Grid>
      </Grid>
    );
  }

  return (
    <Card sx={{ mr: 2, borderRadius: '8px', boxShadow: 'none', border: '1px solid #dcdcdc' }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', borderBottom: '1px solid #dcdcdc' }}>
          <Avatar
            alt={user.name}
            imgProps={{ referrerPolicy: 'no-referrer' }}
            src={user.profilePic || undefined}
            sx={{ width: 88, height: 88, fontSize: '30px', bgcolor: 'primary.main' }}
          >
            {getInitials(user.name)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#1a1a1a' }}>{user.name || '-'}</Typography>
            <Typography sx={{ color: '#5f6368', fontSize: '14px', mb: 1 }}>{user.email || '-'}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {roleName && <Chip label={roleName} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />}
              <Chip size="small" label={user.isActive ? 'Active' : 'Inactive'} color={user.isActive ? 'success' : 'default'} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isAdmin && (
              <EditButton label='Change Password' icon={<LockResetIcon />} onClick={() => setPasswordOpen(true)} />
            )}
            {canUpdateUsers && (
              <EditButton label='Edit Member' onClick={() => setEditOpen(true)} />
            )}
          </Box>
        </Box>

      <Box sx={{ p: 3, bgcolor: '#f5f6f8' }}>
      <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <SectionCard icon={<PersonOutlineIcon fontSize="small" />} title="Personal Information">
          <Row label="Full Name">{user.name || '-'}</Row>
          <Row label="Email">{user.email || '-'}</Row>
          <Row label="Phone">{user.phone || '-'}</Row>
          <Row label="Phone Verified">
            <Chip size="small" variant="outlined" label={user.isPhoneVerified ? 'Verified' : 'Not Verified'} color={user.isPhoneVerified ? 'success' : 'warning'} />
          </Row>
          <Row label="Email Verified">
            <Chip size="small" variant="outlined" label={user.isEmailVerified ? 'Verified' : 'Not Verified'} color={user.isEmailVerified ? 'success' : 'warning'} />
          </Row>
          <Row label="Sign-in Method">
            {signInMethods.length
              ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {signInMethods.map(method => <Chip key={method} size="small" label={method === 'sso' ? 'SSO' : method} sx={{ textTransform: 'capitalize' }} />)}
                </Box>
              : '-'}
          </Row>
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <SectionCard icon={<ShieldOutlinedIcon fontSize="small" />} title="Role & Access">
          <Row label="Role"><Box component="span" sx={{ textTransform: 'capitalize' }}>{roleName || '-'}</Box></Row>
          <Row label="Status">
            <Chip size="small" label={user.isActive ? 'Active' : 'Inactive'} color={user.isActive ? 'success' : 'default'} />
          </Row>
          <Row label="Two-Factor">
            {user.mfa?.enabled
              ? <Chip size="small" color="success" variant="outlined" label={`On · ${(user.mfa.methods || []).join(', ') || 'email'}`} sx={{ textTransform: 'capitalize' }} />
              : <Chip size="small" label="Off" />}
          </Row>
          <Box sx={{ py: 1.75 }}>
            <Typography sx={{ color: '#5f6368', fontSize: '14px', mb: 1 }}>Companies ({accounts.length})</Typography>
            {accounts.length
              ? <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {accounts.map(account => (
                    <Chip key={account._id} size="small" variant="outlined" icon={<BusinessOutlinedIcon />} label={account.name} />
                  ))}
                </Box>
              : <Typography sx={{ fontSize: '14px' }}>-</Typography>}
          </Box>
        </SectionCard>
      </Grid>
      </Grid>
      </Box>
    <UserFormDialog open={editOpen} onClose={() => setEditOpen(false)} user={user} />
    <UserPasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} user={user} />
    </Card>
  );
};

export default UserBasicInfo;
